'use strict';

// external imports
import omniclone from 'omniclone';
import {keys} from 'ts-transformer-keys';
import {encode as encodeHtmlEntities} from 'html-entities';

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import Status from '~/shared/src/mm/Status';
import PropertySet from '~/shared/src/mm/PropertySet';
import Value from '~/shared/src/mm/Value';
import Item from '~/shared/src/ci/Item';
import EnumProperty from '~/shared/src/ci/EnumProperty';
import LocalizableError from '~/shared/src/base/LocalizableError';
import {
    convertToDate,
    convertValueForType,
    convertValueToString
} from '~/shared/src/utilities/valueConverters';
import {validateValueForType} from '~/shared/src/utilities/valueValidators';
import {PropertyMetaDataMapping} from '~/shared/src/utilities/modules/data';


// type definitions
export type PropertyAttributes = {
    id? : number;
    key? : AttributeValue;
    value? : AttributeValue;
    itemId : number;
    propertySetId : number;
    statusId? : number;
    modifierUserId? : string;
    modificationTime? : string | NDate;
    validatorUserId? : string;
    validationTime? : string | NDate;
    validFromTime? : string | NDate;
    validToTime? : string | NDate;
};
export const propertyAttributes = keys<PropertyAttributes>() as string[];
export const isPropertyAttributes = ( propertyAttributesToTest : any ) : propertyAttributesToTest is PropertyAttributes => {
    let retVal = false;

    try {
        if ( typeof propertyAttributesToTest !== 'object' ) {
            throw new Error();
        }

        if ( propertyAttributesToTest.id !== undefined && ( typeof propertyAttributesToTest.id !== 'number' || propertyAttributesToTest.id < 1 ) ) {
            throw new Error();
        }
        // TODO: fix -> not only string! AttributeValue!!!
        if ( propertyAttributesToTest.key !== undefined && typeof propertyAttributesToTest.key !== 'string' ) {
            throw new Error();
        }
        // TODO: fix -> not only string! AttributeValue!!!
        if ( propertyAttributesToTest.value !== undefined && typeof propertyAttributesToTest.value !== 'string' ) {
            throw new Error();
        }
        if ( propertyAttributesToTest.itemId === undefined || typeof propertyAttributesToTest.itemId !== 'number' ) {
            throw new Error();
        }
        if ( propertyAttributesToTest.propertySetId === undefined || typeof propertyAttributesToTest.propertySetId !== 'number' ) {
            throw new Error();
        }
        if ( propertyAttributesToTest.statusId !== undefined && typeof propertyAttributesToTest.statusId !== 'number' ) {
            throw new Error();
        }
        if ( propertyAttributesToTest.modifierUserId !== undefined && typeof propertyAttributesToTest.modifierUserId !== 'string' ) {
            throw new Error();
        }
        if ( propertyAttributesToTest.modificationTime !== undefined && typeof propertyAttributesToTest.modificationTime !== 'string' && !( propertyAttributesToTest.modificationTime instanceof Date ) && propertyAttributesToTest.modificationTime !== 'null' ) {
            throw new Error();
        }
        if ( propertyAttributesToTest.validatorUserId !== undefined && typeof propertyAttributesToTest.validatorUserId !== 'string' ) {
            throw new Error();
        }
        if ( propertyAttributesToTest.validationTime !== undefined && typeof propertyAttributesToTest.validationTime !== 'string' && !( propertyAttributesToTest.validationTime instanceof Date ) && propertyAttributesToTest.validationTime !== 'null' ) {
            throw new Error();
        }
        if ( propertyAttributesToTest.validFromTime !== undefined && typeof propertyAttributesToTest.validFromTime !== 'string' && !( propertyAttributesToTest.validFromTime instanceof Date ) && propertyAttributesToTest.validFromTime !== 'null' ) {
            throw new Error();
        }
        if ( propertyAttributesToTest.validToTime !== undefined && typeof propertyAttributesToTest.validToTime !== 'string' && !( propertyAttributesToTest.validToTime instanceof Date ) && propertyAttributesToTest.validToTime !== 'null' ) {
            throw new Error();
        }

        retVal = true;
    }
    catch ( e ) {
        /*empty*/
    }

    return retVal;
};

export type PropertyJsonAttributes = Omit<PropertyAttributes, 'key' | 'value'>
    & Required<Pick<PropertyAttributes, 'id'>>
    & { key? : string }
    & { value? : string };
export const propertyJsonAttributes = keys<PropertyJsonAttributes>() as string[];
export const isPropertyJsonAttributes = ( propertyJsonAttributesToTest : any ) : propertyJsonAttributesToTest is PropertyJsonAttributes => {
    let retVal = false;

    try {
        if ( typeof propertyJsonAttributesToTest !== 'object' ) {
            throw new Error();
        }

        if ( propertyJsonAttributesToTest.id === undefined || typeof propertyJsonAttributesToTest.id !== 'number' ) {
            throw new Error();
        }
        if ( propertyJsonAttributesToTest.key !== undefined && typeof propertyJsonAttributesToTest.key !== 'string' ) {
            throw new Error();
        }
        if ( propertyJsonAttributesToTest.value !== undefined && typeof propertyJsonAttributesToTest.value !== 'string' ) {
            throw new Error();
        }
        if ( propertyJsonAttributesToTest.itemId === undefined || typeof propertyJsonAttributesToTest.itemId !== 'number' ) {
            throw new Error();
        }
        if ( propertyJsonAttributesToTest.propertySetId === undefined || typeof propertyJsonAttributesToTest.propertySetId !== 'number' ) {
            throw new Error();
        }
        if ( propertyJsonAttributesToTest.statusId !== undefined && typeof propertyJsonAttributesToTest.statusId !== 'number' ) {
            throw new Error();
        }
        if ( propertyJsonAttributesToTest.modifierUserId !== undefined && typeof propertyJsonAttributesToTest.modifierUserId !== 'string' ) {
            throw new Error();
        }
        if ( propertyJsonAttributesToTest.modificationTime !== undefined && typeof propertyJsonAttributesToTest.modificationTime !== 'string' && !( propertyJsonAttributesToTest.modificationTime instanceof Date ) && propertyJsonAttributesToTest.modificationTime !== 'null' ) {
            throw new Error();
        }
        if ( propertyJsonAttributesToTest.validatorUserId !== undefined && typeof propertyJsonAttributesToTest.validatorUserId !== 'string' ) {
            throw new Error();
        }
        if ( propertyJsonAttributesToTest.validationTime !== undefined && typeof propertyJsonAttributesToTest.validationTime !== 'string' && !( propertyJsonAttributesToTest.validationTime instanceof Date ) && propertyJsonAttributesToTest.validationTime !== 'null' ) {
            throw new Error();
        }
        if ( propertyJsonAttributesToTest.validFromTime !== undefined && typeof propertyJsonAttributesToTest.validFromTime !== 'string' && !( propertyJsonAttributesToTest.validFromTime instanceof Date ) && propertyJsonAttributesToTest.validFromTime !== 'null' ) {
            throw new Error();
        }
        if ( propertyJsonAttributesToTest.validToTime !== undefined && typeof propertyJsonAttributesToTest.validToTime !== 'string' && !( propertyJsonAttributesToTest.validToTime instanceof Date ) && propertyJsonAttributesToTest.validToTime !== 'null' ) {
            throw new Error();
        }

        retVal = true;
    }
    catch ( e ) {
        /*empty*/
    }

    return retVal;
};

export type PropertyAttributesSettable = Partial<Pick<PropertyAttributes, 'key' | 'value' | 'statusId'> & { orderNum : number }>;
export const propertyAttributesSettable = keys<PropertyAttributesSettable>() as string[];

export type PropertyAttributesForCreation = Required<Pick<PropertyAttributes, 'value' | 'itemId' | 'propertySetId' | 'statusId'>>
    & Partial<Pick<PropertyAttributes, 'key'>>;
export const propertyAttributesForCreation = keys<PropertyAttributesForCreation>() as string[];

export type PropertyAttributesForUpdate = Partial<Pick<PropertyAttributes, 'key' | 'value' | 'statusId'>>;
export const propertyAttributesForUpdate = keys<PropertyAttributesForUpdate>() as string[];

/**
 * The Property class.
 */
class Property {

    /**
     * The unique Id counter for Properties
     */
    static uniqueIdCount = -1;


    /*** ATTRIBUTES ********************************************************************************************************************/

    /**
     * The ID of the Property
     */
    protected _id : number;
    get id() {
        return this._id;
    }

    /**
     * The Key of the Property
     * Only applicable if the property is an enumerated one
     */
    protected _key : AttributeValue = '';
    get key() {
        return this._key;
    }

    set key( key : AttributeValue ) {
        const propertySet = this.propertySet;

        if ( key !== '' && key !== undefined && key !== null ) {
            if ( propertySet.isKeyOrdered && typeof key === 'string' && propertySet.keyFromStringRegExp?.exec( key.trim() ) ) {
                let orderNum : number;

                ( { orderNum, key } = this.getOrderNumAndKey( key.trim() ) );

                if ( orderNum !== this._orderNum ) {
                    this.orderNum = orderNum;
                }
            }
            else if ( typeof key === 'string' ) {
                key = convertValueForType( key.trim(), propertySet.keyType );
            }
        }

        if ( key !== this._key ) {
            this.validateKey( key );

            this.handleModified( 'key', key );

            this._key = key;

            if ( this._parent instanceof EnumProperty ) {
                this._parent.updatePropertyKey( this );
            }
        }
    }

    get stringKey() : string {
        return ( this.isDeleted() ? 'deleted_' : '' ) + this.propertySet.getStringKey( { key: this._key, orderNum: this._orderNum } );
    }

    /**
     * The Value of the Property
     */
    protected _value : AttributeValue = '';
    get value() {
        return this._value;
    }

    set value( value : AttributeValue ) {
        const propertySet = this.propertySet;

        if ( value !== '' && value !== undefined && value !== null ) {
            if ( propertySet.isValueOrdered && typeof value === 'string' && propertySet.valueFromStringRegExp?.exec( value ) ) {
                let orderNum : number;

                ( { orderNum, value } = this.getOrderNumAndValue( value ) );

                if ( orderNum !== this._orderNum ) {
                    this.orderNum = orderNum;
                }
            }
            else if ( typeof value === 'string' ) {
                value = convertValueForType( value, propertySet.valueType );
            }
        }

        if (
            ( this.propertySet.valueType === 'JSON' && (
                    value !== '' && this._value === ''
                    || JSON.stringify( JSON.parse( value as string ) ) !== JSON.stringify( JSON.parse( this._value as string ) )
                )
            )
            || ( this.propertySet.valueType !== 'JSON' && value !== this._value )
        ) {
            this.validateValue( value );

            this.handleModified( 'value', value );

            this._value = value;
        }
    }

    get stringValue() : string {
        let value = this._value;

        if ( this.propertySet.valueType === 'JSON' ) {
            try {
                value = JSON.stringify( JSON.parse( value as string ) );
            }
            catch ( e ) {
                value = this._value;
            }
        }

        return this.propertySet.getStringValue( { value, orderNum: this._orderNum } );
    }

    protected _orderNum : number = 0;
    get orderNum() {
        return this._orderNum;
    }

    set orderNum( orderNum : number ) {
        if ( orderNum !== this._orderNum ) {
            this.validateOrderNum( orderNum );

            this.handleModified( 'orderNum', orderNum );

            // reorder
            if ( this._parent instanceof EnumProperty && !this._parent.isReorderingProperties ) {
                this._parent.reorderProperties( this, orderNum );
            }
            else {
                this._orderNum = orderNum;

                if ( this.propertySet.isKeyOrdered && this._parent instanceof EnumProperty ) {
                    this._parent.updatePropertyKey( this );
                }
            }
        }
    }

    /**
     * The ItemId the Property belongs to.
     */
    protected _itemId : number;
    get itemId() : number {
        return this._itemId;
    }

    /**
     * The PropertySetId of the Property.
     */
    protected _propertySetId : number;
    get propertySetId() {
        return this._propertySetId;
    }

    /**
     * The StatusID of the Property.
     */
    protected _statusId : number;
    get statusId() : number {
        return this._statusId;
    }

    set statusId( statusId : number ) {
        if ( statusId !== this._statusId ) {
            this.validateStatusId( statusId );

            this.handleModified( 'statusId', statusId );

            this._statusId = statusId;

            if ( this._parent instanceof EnumProperty ) {
                this._parent.updatePropertyKey( this, this.stringKey );

                // reorder
                if ( this.isDeleted() && !this._parent.isReorderingProperties ) {
                    this._parent.reorderProperties();
                }
            }
        }
    }

    /**
     * The UserID of the last modifier of the Property
     */
    protected _modifierUserId : string = '';
    get modifierUserId() : string {
        return this._modifierUserId;
    }

    /**
     * The last modification date of the Property
     */
    protected _modificationTime : NDate = null;
    get modificationTime() : NDate {
        return this._modificationTime;
    }

    /**
     * The UserID of the validator of the Property
     */
    protected _validatorUserId : string = '';
    get validatorUserId() : string {
        return this._validatorUserId;
    }

    /**
     * The last validation date of the Property
     */
    protected _validationTime : NDate = null;
    get validationTime() : NDate {
        return this._validationTime;
    }

    /**
     * The valid from date of a History-Property
     */
    protected _validFromTime : NDate = null;
    get validFromTime() {
        return this._validFromTime;
    }

    /**
     * The valid to date of a History-Property
     */
    protected _validToTime : NDate = null;
    get validToTime() {
        return this._validToTime;
    }

    /*** ALIASES ***********************************************************************************************************************/

    get status() : Status {
        return metaModel[ this._schema ].getStatus( this._statusId )!;
    }

    /**
     * The PropertySet of this Property.
     */
    get propertySet() : PropertySet {
        return metaModel[ this._schema ].getPropertySet( this._propertySetId )!;
    }

    /**
     * The label for a valueSet value
     */
    get valueLabel() : string | undefined {
        return this.propertySet.valueSetId ? metaModel[ this._schema ]?.getValueSetValue( this.propertySet.valueSetId, String( this._value ) )?.label : undefined;
    }


    /*************************************************************************************************************************************/

    /**
     * The Item or EnumProperty this Property belongs to
     */
    protected _parent : Item | EnumProperty;
    get parent() {
        return this._parent;
    }

    set parent( parent : Item | EnumProperty ) {
        if (
            ( this._parent instanceof Item && parent instanceof Item )
            || ( this._parent instanceof EnumProperty && parent instanceof EnumProperty )
        ) {
            this._parent = parent;
        }
    }


    /**
     * The database schema this Property belongs to
     */
    protected _schema : Schema;
    get schema() {
        return this._schema;
    }

    /**
     * Map containing the modified attributes with their initial values
     */
    protected _attributesModified : Map<keyof PropertyAttributesSettable, AttributeValue>;
    get attributesModified() {
        return this._attributesModified;
    }

    /**
     *The constructor.
     *
     * @param parent                    The parent Item or EnumProperty this belongs to
     * @param attributes                An object containing attribute-value pairs for this
     *
     */
    constructor( parent : Item | EnumProperty, attributes : PropertyAttributes ) {

        if ( attributes.id && attributes.id < 1 ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attributes.validation.id.invalid' );
        }

        if ( attributes.itemId === 0 ) {
            throw new Error( 'YN.base.dataObject.property.attributes.validation.itemId.isEmpty' );
        }

        if ( attributes.propertySetId === 0 ) {
            throw new Error( 'YN.base.dataObject.property.attributes.validation.propertySetId.isEmpty' );
        }

        if ( attributes.propertySetId !== metaModel[ parent.schema ].getPropertySet( attributes.propertySetId )?.id ) {
            throw new LocalizableError(
                'YN.base.dataObject.property.attributes.validation.propertySetId.invalid',
                { propertySetId: String( attributes.propertySetId ), schema: parent.schema }
            );
        }

        this._parent = parent;

        this._schema = parent.schema;

        if ( attributes.id ) {
            this._id = attributes.id;
        }
        else {
            this._id = Property.uniqueIdCount--;
        }

        this._propertySetId = attributes.propertySetId;
        this._itemId = attributes.itemId;

        const propertySet = this.propertySet;

        this._orderNum = 0;
        if ( propertySet.isEnumerated && attributes.key !== undefined && attributes.key !== null ) {
            let keyToSet : AttributeValue | undefined,
                orderNumToSet : number | undefined;

            if ( propertySet.isKeyOrdered ) {
                if ( typeof attributes.key !== 'string' ) {
                    throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.key.mustBeStringForKeyOrderedEnum' );
                }

                const { orderNum, key } = this.getOrderNumAndKey( attributes.key.trim() );

                orderNumToSet = orderNum;
                keyToSet = key;
            }
            else if ( typeof attributes.key === 'string' ) {
                keyToSet = convertValueForType( attributes.key.trim(), propertySet.keyType );
            }
            else {
                keyToSet = attributes.key;
            }

            if ( orderNumToSet ) {
                this.validateOrderNum( orderNumToSet );
                this._orderNum = orderNumToSet
            }

            if ( keyToSet !== undefined && keyToSet !== null && keyToSet !== '' ) {
                this.validateKey( keyToSet );
                this._key = keyToSet
            }
        }

        if ( attributes.value !== undefined && attributes.value !== null ) {
            let valueToSet : AttributeValue | undefined,
                orderNumToSet : number | undefined;

            if ( propertySet.isEnumerated && propertySet.isValueOrdered ) {
                if ( typeof attributes.value !== 'string' ) {
                    throw new LocalizableError(
                        'YN.base.dataObject.property.attributes.validation.value.mustBeStringForValueOrderedEnum'
                    );
                }

                const { orderNum, value } = this.getOrderNumAndValue( attributes.value );

                orderNumToSet = orderNum;
                valueToSet = value;
            }
            else if ( typeof attributes.value === 'string' ) {
                valueToSet = convertValueForType( attributes.value, propertySet.valueType );
            }
            else {
                valueToSet = attributes.value;
            }

            if ( orderNumToSet ) {
                this.validateOrderNum( orderNumToSet );
                this._orderNum = orderNumToSet
            }

            if ( valueToSet !== undefined && valueToSet !== null && valueToSet !== '' ) {
                this.validateValue( valueToSet );
                this._value = valueToSet;
            }
        }

        // get new orderNum if needed and no one set (===0)
        if ( this.isNew() && ( propertySet.isKeyOrdered || propertySet.isValueOrdered ) && !this._orderNum && this._parent instanceof EnumProperty ) {
            this._orderNum = this._parent.getMaxOrderNum() + 1;
        }

        if ( attributes.statusId ) {
            this._statusId = attributes.statusId;
        }
        else {
            this._statusId = parent instanceof Item
                ? parent.statusId
                : parent.parent.statusId;
        }

        if ( attributes.modifierUserId ) {
            this._modifierUserId = attributes.modifierUserId;
        }
        if ( attributes.modificationTime ) {
            if ( typeof attributes.modificationTime === 'string' ) {
                this._modificationTime = convertToDate( attributes.modificationTime );
            }
            else {
                this._modificationTime = attributes.modificationTime;
            }
        }
        if ( attributes.validatorUserId ) {
            this._validatorUserId = attributes.validatorUserId;
        }
        if ( attributes.validationTime ) {
            if ( typeof attributes.validationTime === 'string' ) {
                this._validationTime = convertToDate( attributes.validationTime );
            }
            else {
                this._validationTime = attributes.validationTime;
            }
        }
        if ( attributes.validFromTime ) {
            if ( typeof attributes.validFromTime === 'string' ) {
                this._validFromTime = convertToDate( attributes.validFromTime );
            }
            else {
                this._validFromTime = attributes.validFromTime;
            }
        }
        if ( attributes.validToTime ) {
            if ( typeof attributes.validToTime === 'string' ) {
                this._validToTime = convertToDate( attributes.validToTime );
            }
            else {
                this._validToTime = attributes.validToTime;
            }
        }

        this._attributesModified = new Map();
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param includeAttributes   The state of the attributes to include in result.
     */
    toJSON( includeAttributes : 'all' | 'modified' = 'all' ) : PropertyJsonAttributes {
        // required attributes
        const retVal : PropertyJsonAttributes = {
                  id           : this._id,
                  itemId       : this._itemId,
                  propertySetId: this._propertySetId
              },
              isNew                           = this.isNew(),
              propertySet                     = this.propertySet;

        if (
            propertySet.isEnumerated && (
                isNew
                || includeAttributes !== 'modified'
                || this._attributesModified.has( 'key' )
                || ( propertySet.isKeyOrdered && this._attributesModified.has( 'orderNum' ) )
            )
        ) {
            retVal.key = this.stringKey;
        }

        if (
            isNew
            || includeAttributes !== 'modified'
            || this._attributesModified.has( 'value' )
            || ( propertySet.isValueOrdered && this._attributesModified.has( 'orderNum' ) )
        ) {
            retVal.value = this.stringValue;
        }

        if (
            isNew
            || includeAttributes !== 'modified'
            || this._attributesModified.has( 'statusId' )
        ) {
            retVal.statusId = this._statusId;
        }

        // not settable attributes
        if ( includeAttributes !== 'modified' ) {
            retVal.modifierUserId = this.modifierUserId;

            // only active attributes
            if ( !this.isHistorized() ) {
                retVal.modificationTime = convertValueToString( this.modificationTime, 'TIMESTAMP' );
                retVal.validatorUserId = this.validatorUserId;
                retVal.validationTime = convertValueToString( this.validationTime, 'TIMESTAMP' );
            }

            // only history attributes
            else {
                retVal.validFromTime = convertValueToString( this._validFromTime, 'TIMESTAMP' );
                retVal.validToTime = convertValueToString( this._validToTime, 'TIMESTAMP' );
            }
        }

        return retVal;
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param includeAttributes   The state of the attributes to include in result.
     */
    toUpdateJSON( includeAttributes : 'all' | 'modified' = 'all' ) : PropertyAttributesSettable {
        // required attributes
        const retVal : PropertyAttributesSettable = {},
              isNew                               = this.isNew(),
              propertySet                         = this.propertySet;

        if (
            propertySet.isEnumerated && (
                isNew
                || includeAttributes !== 'modified'
                || this._attributesModified.has( 'key' )
                || ( propertySet.isKeyOrdered && this._attributesModified.has( 'orderNum' ) )
            )
        ) {
            retVal.key = this.stringKey;
        }

        if (
            isNew
            || includeAttributes !== 'modified'
            || this._attributesModified.has( 'value' )
            || ( propertySet.isValueOrdered && this._attributesModified.has( 'orderNum' ) )
        ) {
            retVal.value = this.stringValue;
        }

        if (
            isNew
            || includeAttributes !== 'modified'
            || this._attributesModified.has( 'statusId' )
        ) {
            retVal.statusId = this._statusId;
        }

        return retVal;
    }

    /**
     * Builds and returns the signature string for this
     */
    toString() : string {
        let retVal : string;

        if ( this._parent instanceof EnumProperty ) {
            retVal = `Property for key '${this.stringKey}'`;
        }
        else {
            retVal = `Property '${this.propertySet.name}'`;
        }

        retVal += ` of ${this._parent}`;

        return retVal;
    }

    /**
     * Returns a string containing the XML representation of this
     * @param {boolean} [excludeDeleted]
     */
    toXML( excludeDeleted : boolean = false, indent : number = 0 ) : string {
        const propertySet = this.propertySet,
              valueType   = propertySet.valueType;

        let retVal = '';

        if ( !excludeDeleted || !this.isDeleted() ) {
            retVal = `<property name='${encodeHtmlEntities( this.propertySet.name, { level: 'xml' } )}'`.indent( indent );

            if ( this._parent instanceof EnumProperty ) {
                retVal += ` key='${encodeHtmlEntities( this.stringKey, { level: 'xml' } )}'`;
            }

            retVal += ` status='${encodeHtmlEntities( this.status.name, { level: 'xml' } )}'>`;

            if ( valueType === 'JSON' || valueType === 'STRING' || valueType === 'TEXT' || valueType === 'TIMERULE' ) {
                let xmlValue = this.stringValue;

                // replace ]]> sequence
                xmlValue = xmlValue.replaceAll( /]]>/g, ']]]]><![CDATA[>' );

                retVal += `<![CDATA[${xmlValue}]]>`;
            }
            else {
                retVal += this.stringValue;
            }

            retVal += '</property>';
        }

        return retVal;
    }

    /**
     * Resets all settable attributes to their initial values.
     */
    reset() : Property {
        if ( this._attributesModified.has( 'key' ) ) {
            this._key = this._attributesModified.get( 'key' ) as AttributeValue;
        }
        if ( this._attributesModified.has( 'value' ) ) {
            this._value = this._attributesModified.get( 'value' ) as AttributeValue;
        }
        if ( this._attributesModified.has( 'statusId' ) ) {
            this._statusId = this._attributesModified.get( 'statusId' ) as number;
        }
        if ( this._attributesModified.has( 'orderNum' ) ) {
            this._orderNum = this._attributesModified.get( 'orderNum' ) as number;
        }

        this._attributesModified = new Map();

        return this;
    }

    /**
     * Commits all changes of settable attributes.
     */
    commit( propertyMetaDataMapping? : PropertyMetaDataMapping ) : Property {

        // update id and metaData if new and mapped one is given
        if ( propertyMetaDataMapping?.[ this._id ] ) {
            this._modifierUserId = propertyMetaDataMapping[ this._id ].modifierUserId;
            this._modificationTime = convertToDate( propertyMetaDataMapping[ this._id ].modificationTime );
            this._validatorUserId = propertyMetaDataMapping[ this._id ].validatorUserId;
            this._validationTime = convertToDate( propertyMetaDataMapping[ this._id ].validationTime );

            if ( this.isNew() ) {
                this._itemId = propertyMetaDataMapping[ this._id ].itemId;
                this._id = propertyMetaDataMapping[ this._id ].id;
            }
        }

        this._attributesModified = new Map();

        return this;
    }

    /**
     * Resets the given settable attribute to its initial value.
     *
     * @param {keyof PropertyAttributesSettable} attribute
     */
    resetAttribute( attribute : keyof PropertyAttributesSettable ) {
        if ( this._attributesModified.has( attribute ) ) {
            switch ( attribute ) {
                case 'key':
                    this._key = this._attributesModified.get( 'key' ) as string;
                    break;

                case 'value':
                    this._value = this._attributesModified.get( 'value' ) as AttributeValue;
                    break;

                case 'statusId':
                    this._statusId = this._attributesModified.get( 'statusId' ) as number;
                    break;

                case 'orderNum':
                    this._orderNum = this._attributesModified.get( 'orderNum' ) as number;
                    break;
            }

            this._attributesModified.delete( attribute );
        }
    }

    /**
     * Apply given attributes to this property.
     *
     * @param attributes An object containing attribute-value pairs to be applied to this property
     */
    update( attributes : PropertyAttributesSettable = {} ) : Property {
        const propertySet = this.propertySet;

        // apply data
        if ( attributes.statusId !== undefined && attributes.statusId !== this._statusId ) {
            this.statusId = attributes.statusId;
        }

        if ( attributes.key !== undefined && attributes.key !== null ) {
            let key : AttributeValue;

            if ( propertySet.isKeyOrdered && typeof attributes.key === 'string' && propertySet.keyFromStringRegExp?.exec( attributes.key ) ) {
                let orderNum : number;

                ( { orderNum, key } = this.getOrderNumAndKey( attributes.key ) );

                if ( orderNum !== this._orderNum ) {
                    this.orderNum = orderNum;
                }
            }
            else if ( typeof attributes.key === 'string' ) {
                key = convertValueForType( attributes.key, propertySet.keyType );
            }
            else {
                key = attributes.key;
            }

            if ( key !== this._key ) {
                this.key = key;
            }
        }

        if ( attributes.value !== undefined && attributes.value !== null ) {
            let value : AttributeValue;

            if ( propertySet.isEnumerated && propertySet.isValueOrdered && typeof attributes.value === 'string' && propertySet.valueFromStringRegExp?.exec( attributes.value ) ) {
                let orderNum : number;

                ( { orderNum, value } = this.getOrderNumAndValue( attributes.value ) );

                if ( orderNum !== this._orderNum ) {
                    this.orderNum = orderNum;
                }
            }
            else if ( typeof attributes.value === 'string' ) {
                value = convertValueForType( attributes.value, propertySet.valueType );
            }
            else {
                value = attributes.value;
            }

            if ( value !== this._value ) {
                this.value = value;
            }
        }

        if ( attributes.orderNum !== undefined && attributes.orderNum !== null && attributes.orderNum !== this._orderNum ) {
            this.orderNum = attributes.orderNum;
        }

        return this;
    }

    /**
     * Creates and returns a clone of this with given attributes applied.
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     */
    clone( attributes : PropertyAttributesSettable = {} ) : Property {
        const clone : Property = omniclone(
            this,
            {
                invokeConstructors: false,
                setPrototype      : true,
                copyGettersSetters: true
            },
            ( node : any ) => {
                // do not clone parent
                if ( node instanceof EnumProperty || node instanceof Item ) {
                    return node;
                }
                else {
                    return undefined;
                }
            }
        );

        if ( Object.keys( attributes ).length ) {
            clone.update( attributes );
        }

        return clone;
    }

    /**
     * Checks if this is new.
     */
    isNew() : boolean {
        return this._id < 0;
    }

    /**
     * Checks if this is historized.
     */
    isHistorized() : boolean {
        return this._validFromTime !== null;
    }

    /**
     * Checks if this is deleted.
     */
    isDeleted() : boolean {
        return metaModel[ this._schema ].getDeletedStatus().map( s => s.id ).includes( this._statusId );
    }

    /**
     * Checks if this is modified.
     */
    isModified() : boolean {
        return this._attributesModified.size > 0;
    }

    /**
     * Checks if this is valid.
     */
    isValid() : boolean {
        let retVal = true;

        const propertySet = this.propertySet;

        // new
        if ( this.isNew() ) {
            // key settable and empty
            if ( propertySet.isKeySettable && this._key === '' ) {
                retVal = false;
            }
            // key not settable and not empty
            else if ( !propertySet.isKeySettable && this._key !== '' ) {
                retVal = false;
            }

            // value settable and empty
            if ( propertySet.isValueSettable && this._value === '' ) {
                retVal = false;
            }
            // value not settable and not empty
            else if ( !propertySet.isValueSettable && this._value !== '' ) {
                retVal = false;
            }

            // orderNum settable and zero
            if ( ( propertySet.isKeyOrdered || propertySet.isValueOrdered ) && this._orderNum === 0 ) {
                retVal = false;
            }
            // orderNum not settable and not zero
            else if ( !propertySet.isKeyOrdered && !propertySet.isValueOrdered && this._orderNum !== 0 ) {
                retVal = false;
            }

            // deleted
            if ( this.isDeleted() ) {
                retVal = false;
            }
        }

        // modified
        else {
            // key is modified and settable and empty
            if ( this.isAttributeModified( 'key' ) && propertySet.isKeySettable && this._key === '' ) {
                retVal = false;
            }
            // key is modified and not settable and not empty
            else if ( this.isAttributeModified( 'key' ) && !propertySet.isKeySettable && this._key !== '' ) {
                retVal = false;
            }

            // value is modified and settable and empty
            if ( this.isAttributeModified( 'value' ) && propertySet.isValueSettable && this._value === '' ) {
                retVal = false;
            }
            // value is modified and not settable and not empty
            else if ( this.isAttributeModified( 'value' ) && !propertySet.isValueSettable && this._value !== '' ) {
                retVal = false;
            }

            // orderNum is modified and settable and zero
            if ( this.isAttributeModified( 'orderNum' ) && ( propertySet.isKeyOrdered || propertySet.isValueOrdered ) && this._orderNum === 0 ) {
                retVal = false;
            }
            // orderNum is modified and not settable and not zero
            else if ( this.isAttributeModified( 'orderNum' ) && !propertySet.isKeyOrdered && !propertySet.isValueOrdered && this._orderNum !== 0 ) {
                retVal = false;
            }

            // deleted and modified
            if ( this.isDeleted() && ( this.isAttributeModified( 'key' ) || this.isAttributeModified( 'value' ) || this.isAttributeModified( 'orderNum' ) ) ) {
                retVal = false;
            }
        }

        return retVal;
    }

    /**
     * Checks if the given attribute is modified or not.
     *
     * @param attribute        The attribute to check if it is modified
     */
    isAttributeModified( attribute : keyof PropertyAttributesSettable ) : boolean {
        return this._attributesModified.has( attribute );
    }

    /**
     * Gets if the (original) unmodified value for given attribute is modified .
     *
     * @param attribute        The attribute to get value for
     */
    getAttributeOriginalValue( attribute : keyof PropertyAttributesSettable ) : AttributeValue {
        return this._attributesModified.get( attribute ) ?? this[ attribute ];
    }

    protected getOrderNumAndKey( stringKey : string ) : { orderNum : number; key : AttributeValue; } {
        const retVal : { orderNum : number; key : AttributeValue; } = {
                  orderNum: 0,
                  key     : ''
              },
              propertySet                                           = this.propertySet,
              keyFromStringRegExp                                   = propertySet.keyFromStringRegExp;

        if ( keyFromStringRegExp ) {
            const match = keyFromStringRegExp.exec( stringKey );

            if ( match ) {
                retVal.orderNum = Number( match.groups?.orderNum ?? '0' );
                retVal.key = convertValueForType( match.groups?.key ?? '', propertySet.keyType );
            }
            else {
                throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.key.doesntMatchKeyDefinition' );
            }
        }
        else {
            retVal.key = convertValueForType( stringKey ?? '', propertySet.keyType );
        }

        return retVal
    }

    protected getOrderNumAndValue( stringValue : string ) : { orderNum : number; value : AttributeValue; } {
        const retVal : { orderNum : number; value : AttributeValue; } = {
                  orderNum: 0,
                  value   : ''
              },
              propertySet                                             = this.propertySet,
              valueFromStringRegExp                                   = propertySet.valueFromStringRegExp;

        if ( valueFromStringRegExp ) {
            const match = valueFromStringRegExp.exec( stringValue );

            if ( match ) {
                retVal.orderNum = Number( match.groups?.orderNum ?? '0' );
                retVal.value = convertValueForType( match.groups?.value ?? '', propertySet.valueType );
            }
            else {
                throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.value.doesntMatchValueDefinition' );
            }
        }
        else {
            retVal.value = convertValueForType( stringValue ?? '', propertySet.valueType );
        }

        return retVal
    }

    /**
     * Handles the modified attributes list.
     *
     * @param attribute         The attribute
     * @param value             The value for the attribute
     */
    protected handleModified( attribute : keyof PropertyAttributesSettable, value : AttributeValue ) {
        const valueType            = this.propertySet.valueType,
              compareValue         = attribute === 'key' || attribute === 'value'
                  ? convertValueToString( value, valueType )
                  : value,
              currentCompareValue  = attribute === 'key' || attribute === 'value'
                  ? convertValueToString( this[ attribute ], valueType )
                  : this[ attribute ],
              modifiedCompareValue = attribute === 'key' || attribute === 'value'
                  ? convertValueToString( this._attributesModified.get( attribute )!, valueType )
                  : this._attributesModified.get( attribute );

        // set as modified
        if ( !this._attributesModified.has( attribute ) && compareValue !== currentCompareValue ) {
            this._attributesModified.set( attribute, this[ attribute ] );
        }

        // unset as modified
        else if ( this._attributesModified.has( attribute ) && compareValue === modifiedCompareValue ) {
            this._attributesModified.delete( attribute );
        }
    }

    protected checkStatus() {
        if ( this.isDeleted() ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.couldNotChangeDeleted', undefined, undefined, undefined, 20111 );
        }
        else if (
            ( this._parent instanceof Item && this._parent.isDeleted() )
            || ( this._parent instanceof EnumProperty && this._parent.parent.isDeleted() )
        ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.couldNotChangeOfDeleted', undefined, undefined, undefined, 20112 );
        }
    }

    /**
     * Validates the given statusId
     */
    protected validateStatusId( statusId : number ) {
        if ( !metaModel[ this._schema ].getStatus( statusId ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.statusId.notExists', undefined, undefined, undefined, 20121 );
        }

        if (
            (
                statusId === metaModel[ this._schema ].getStatus( 'history' )?.id
                || statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id
            )
            && (
                this._attributesModified.size > 1
                || ( this._attributesModified.size === 1 && !this._attributesModified.has( 'statusId' ) )
            )
        ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.couldNotDeleteChanged', undefined, undefined, undefined, 20122 );
        }
    }

    /**
     * Validates the key for this
     */
    protected validateKey( key : AttributeValue ) {
        this.checkStatus();

        // enumerated
        if ( this._parent instanceof Item ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.key.notEnumerated', undefined, undefined, undefined, 20131 );
        }

        // settable
        if ( !this.propertySet.isKeySettable ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.key.notSettable', undefined, undefined, undefined, 20132 );
        }
        // empty
        else if ( key === undefined || key === null || key === '' ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.key.isEmpty', undefined, undefined, undefined, 20133 );
        }

        // unique
        if ( this._parent instanceof EnumProperty && !this._parent.isKeyUnique( key, this ) ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.key.isNotUnique', undefined, undefined, undefined, 20134 );
        }

        // stringKey length
        if ( this.propertySet.getStringKey( { key, orderNum: this._orderNum } ).length > 255 ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.key.tooLong' );
        }

        try {
            validateValueForType( key, this.propertySet.keyType, true );
        }
        catch ( e ) {
            if ( e instanceof Error ) {
                throw new LocalizableError( e.name, undefined, undefined, undefined, 20136 );
            }
        }
    }

    /**
     * Validates the value of this
     */
    protected validateValue( value : AttributeValue ) {
        this.checkStatus();

        const propertySet = this.propertySet,
              valueSet    = propertySet.valueSet,
              valueType   = propertySet.valueType;

        // settable
        if ( !propertySet.isValueSettable ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.value.notSettable', undefined, undefined, undefined, 20142 );
        }
        // empty
        else if ( value === undefined || value === null || value === '' ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.value.empty', undefined, undefined, undefined, 20143 );
        }

        if ( valueSet ) {
            const rawValueSetValues = valueSet.values,
                  valueSetValues    = rawValueSetValues.map( ( valueSetValue : Value ) => {
                      return convertValueForType( valueSetValue.value, valueType );
                  } );

            if ( !valueSetValues.includes( value ) ) {
                throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.value.doesntMatchValueSet', { value: convertValueToString( value, valueType ) }, undefined, undefined, 20145 );
            }
        }

        try {
            validateValueForType( value, valueType, true );
        }
        catch ( e ) {
            if ( e instanceof LocalizableError ) {
                e.number = 20146;
                throw e;
            }
            else if ( e instanceof Error ) {
                throw new LocalizableError( e.name, undefined, undefined, undefined, 20146 );
            }
        }

        if ( propertySet.valueMin && value < convertValueForType( propertySet.valueMin, valueType )! ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.value.lesserThanValueMin', { value: convertValueToString( value, valueType ), valueMin: convertValueToString( propertySet.valueMin, valueType ) }, undefined, undefined, 20147 );
        }
        if ( propertySet.valueMax && value > convertValueForType( propertySet.valueMax, valueType )! ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.value.greaterThanValueMax', { value: convertValueToString( value, valueType ), valueMax: convertValueToString( propertySet.valueMax, valueType ) }, undefined, undefined, 20148 );
        }
    }

    /**
     * Validates the orderNum of this
     */
    protected validateOrderNum( orderNum : number ) {
        const propertySet = this.propertySet;

        this.checkStatus();

        // enumerated
        if ( this._parent instanceof Item || ( !propertySet.isKeyOrdered && !propertySet.isValueOrdered ) ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.orderNum.notSortedEnumerated', undefined, undefined, undefined, 20151 );
        }

        // stringKey length
        if ( this.propertySet.getStringKey( { key: this._key, orderNum } ).length > 255 ) {
            throw new LocalizableError( 'YN.base.dataObject.property.attributes.validation.key.tooLong' );
        }
    }
}

export default Property;
